import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Alert,
  Button,
  Divider,
  Flex,
  Heading,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz } from 'ag-grid-community';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';
import { useFitGridColumns } from '../fitGridColumns';
import { withFleets } from '../withFleets';

const OWED_STATUSES = ['open', 'give_up'];

export default function Userincidentals({ userId, fleetCode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalOwed, setTotalOwed] = useState(0);
  const [chargingEnabled, setChargingEnabled] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [waiveRow, setWaiveRow] = useState(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [isWaiving, setIsWaiving] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [collectInfo, setCollectInfo] = useState('');
  const columnsFit = useFitGridColumns(false);

  async function submitWaive() {
    if (!waiveRow) return;
    if (!waiveReason.trim()) {
      setError('A reason is required to waive an incidental.');
      return;
    }
    setIsWaiving(true);
    setError('');
    try {
      const query = {};
      if (fleetCode) query.fleetCode = fleetCode;
      await api3Post(
        '/fleets/tolls-queue',
        { action: 'waive', incidentalId: waiveRow.id, reason: waiveReason.trim() },
        query
      );
      setWaiveRow(null);
      setWaiveReason('');
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Failed to waive incidental');
    }
    setIsWaiving(false);
  }

  async function submitRetry(row) {
    if (!row?.id) return;
    if (!chargingEnabled) {
      setError('Retry is disabled while INCIDENTALS_CHARGING_ENABLED is off.');
      return;
    }
    setRetryingId(row.id);
    setError('');
    try {
      const query = {};
      if (fleetCode) query.fleetCode = fleetCode;
      await api3Post(
        '/fleets/tolls-queue',
        { action: 'retry', incidentalId: row.id },
        query
      );
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Retry charge failed');
      setRefreshKey((k) => k + 1);
    }
    setRetryingId(null);
  }

  async function submitCollectAll() {
    if (!userId) return;
    if (!chargingEnabled) {
      setError('Collect is disabled while INCIDENTALS_CHARGING_ENABLED is off.');
      return;
    }
    setCollecting(true);
    setError('');
    setCollectInfo('');
    try {
      const query = {};
      if (fleetCode) query.fleetCode = fleetCode;
      const data = await api3Post(
        '/fleets/tolls-queue',
        { action: 'collect_driver', driverId: Number(userId) },
        query
      );
      setCollectInfo(data?.message || 'Collect finished');
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setError(e.message || 'Collect failed');
      setRefreshKey((k) => k + 1);
    }
    setCollecting(false);
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!userId || userId <= 0) return;
      setIsLoading(true);
      setError('');
      try {
        const query = { driverId: userId };
        if (fleetCode) query.fleetCode = fleetCode;
        const data = await api3Get('/fleets/tolls-queue', query);
        if (cancelled) return;
        setRows(data?.resp || []);
        setTotalOwed(data?.totalOwed || 0);
        setChargingEnabled(Boolean(data?.chargingEnabled));
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Failed to load incidentals');
          setRows([]);
          setTotalOwed(0);
        }
      }
      if (!cancelled) setIsLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, fleetCode, refreshKey]);

  const columns = [
    {
      headerName: 'Reservation',
      field: 'parent_res_id',
      rowGroup: true,
      hide: true,
    },
    { headerName: 'Created', field: 'created', width: 170 },
    { headerName: 'Category', field: 'fee_category', width: 110, filter: true },
      {
        headerName: 'Owed',
        field: 'owed_amount',
        width: 110,
        aggFunc: 'sum',
        valueFormatter: (p) => (p.value == null ? '' : `$${Number(p.value).toFixed(2)}`),
      },
      {
        headerName: 'Collected',
        field: 'collected_amount',
        width: 120,
        aggFunc: 'sum',
        valueFormatter: (p) => (p.value == null ? '' : `$${Number(p.value).toFixed(2)}`),
      },
    {
      headerName: 'Amount',
      field: 'amount',
      width: 110,
      cellRenderer: (p) => `$${Number(p.value || 0).toFixed(2)}`,
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 120,
      filter: true,
      cellRenderer: (p) =>
        OWED_STATUSES.includes(p.value) ? <b>{p.value}</b> : p.value,
    },
    { headerName: 'Description', field: 'description', minWidth: 160, filter: true },
    { headerName: 'Local time', field: 'transaction_local_datetime', width: 170 },
    { headerName: 'Location', field: 'location', minWidth: 220, filter: true },
    {
      headerName: 'Reservation',
      field: 'res_id',
      width: 150,
      cellRenderer: (p) =>
        p.value ? <NavLink to={withFleets(`/reservation/${p.value}`, fleetCode)}>{p.value}</NavLink> : '',
    },
    { headerName: 'Car', field: 'car_name', width: 130, filter: true },
    { headerName: 'Attempts', field: 'charge_attempt_count', width: 100 },
    { headerName: 'Last attempt', field: 'last_charge_attempt_at', width: 170 },
    { headerName: 'Paid at', field: 'paid_at', width: 170 },
    { headerName: 'Last error', field: 'last_charge_error', minWidth: 160 },
    {
      headerName: '',
      field: 'id',
      width: 200,
      sortable: false,
      cellRenderer: (p) => {
        if (!OWED_STATUSES.includes(p.data?.status)) return '';
        const busy = retryingId === p.data?.id;
        return (
          <Flex gap="0.35rem">
            <Button
              size="small"
              variation="primary"
              isDisabled={!chargingEnabled || busy || isWaiving}
              onClick={() => submitRetry(p.data)}
            >
              {busy ? 'Retrying…' : 'Retry'}
            </Button>
            <Button
              size="small"
              isDisabled={busy || isWaiving}
              onClick={() => {
                setWaiveRow(p.data);
                setWaiveReason('');
              }}
            >
              Waive
            </Button>
          </Flex>
        );
      },
    },
  ];

  const owedCount = rows.filter((r) => OWED_STATUSES.includes(r.status)).length;
  const collectedTotal = rows.reduce(
    (sum, r) => sum + (r.status === 'paid' ? Number(r.amount || 0) : 0),
    0
  );

  return (
    <Flex direction="column">
      <View style={{ padding: '1rem' }}>
        <Divider />
        <Flex direction="column">
          <Heading level={2}>Owed Incidentals</Heading>
          <Text style={{ width: '100%' }}>
            One-off billable expenses for this driver (tolls, cleanup, etc.) and their
            collection status.
          </Text>
          <View>
            <Button onClick={() => setRefreshKey((k) => k + 1)}>
              PRESS TO GET LATEST UPDATES
            </Button>
          </View>
          <Divider />
          {error ? <Alert variation="error">{error}</Alert> : null}
          {isLoading ? (
            <Loadingmini />
          ) : (
            <Flex direction="column">
              <Heading level={4}>
                Total owed: ${Number(totalOwed).toFixed(2)}
                {owedCount ? ` (${owedCount} unpaid)` : ''}
                {collectedTotal > 0
                  ? ` · collected $${collectedTotal.toFixed(2)}`
                  : ''}
              </Heading>
              {owedCount > 0 && chargingEnabled ? (
                <View>
                  <Button
                    variation="primary"
                    isDisabled={collecting || retryingId != null}
                    onClick={submitCollectAll}
                  >
                    {collecting ? 'Collecting…' : 'Collect all unpaid'}
                  </Button>
                </View>
              ) : null}
              {collectInfo ? <Alert variation="info">{collectInfo}</Alert> : null}
              {!chargingEnabled && owedCount > 0 ? (
                <Text>
                  <i>
                    Auto-charging is currently disabled (INCIDENTALS_CHARGING_ENABLED) —
                    these balances are tracked but not being collected.
                  </i>
                </Text>
              ) : null}
              {rows.length === 0 ? (
                <Text>No incidentals recorded for this driver.</Text>
              ) : (
                <div
                  ref={columnsFit.rootRef}
                  className="ag-theme-quartz"
                  style={{ height: 300, width: '100%', marginTop: '0.5rem' }}
                >
                  <AgGridReact
                    rowData={rows}
                    columnDefs={columns}
                    theme={themeQuartz}
                    onGridReady={columnsFit.onGridReady}
                    onFirstDataRendered={columnsFit.onFirstDataRendered}
                    onRowDataUpdated={columnsFit.onRowDataUpdated}
                    autoGroupColumnDef={{
                      headerName: 'Reservation',
                      minWidth: 200,
                      cellRendererParams: {
                        innerRenderer: (params) => {
                          const rid =
                            params.value ||
                            params.node?.allLeafChildren?.[0]?.data?.parent_res_id ||
                            params.data?.parent_res_id;
                          if (rid) return <NavLink to={withFleets(`/reservation/${rid}`, fleetCode)}>{rid}</NavLink>;
                          return rid || '';
                        },
                      },
                    }}
                    groupDisplayType="singleColumn"
                    groupTotalRow="bottom"
                  />
                </div>
              )}
              {waiveRow ? (
                <View
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    padding: '1rem',
                    marginTop: '0.5rem',
                  }}
                >
                  <Heading level={5}>
                    Waive ${Number(waiveRow.amount || 0).toFixed(2)}{' '}
                    {waiveRow.fee_category} (#{waiveRow.id})
                  </Heading>
                  <Text>
                    Marks this incidental as not owed (e.g. already paid outside the
                    platform). It will stop counting toward the driver's balance and
                    will never be auto-charged. This cannot be undone from the portal.
                  </Text>
                  <TextField
                    label="Reason (required)"
                    value={waiveReason}
                    onChange={(e) => setWaiveReason(e.target.value)}
                    placeholder="e.g. Toll already charged via external HCTRA billing"
                  />
                  <Flex direction="row" style={{ marginTop: '0.5rem' }}>
                    <Button
                      variation="primary"
                      isLoading={isWaiving}
                      disabled={!waiveReason.trim()}
                      onClick={submitWaive}
                    >
                      Confirm waive
                    </Button>
                    <Button onClick={() => setWaiveRow(null)}>Cancel</Button>
                  </Flex>
                </View>
              ) : null}
            </Flex>
          )}
          <Divider />
        </Flex>
      </View>
    </Flex>
  );
}
